import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { DownloadButton } from '@/utils/DownloadButton';
import { API_URL } from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import {
  Calendar, Clock, Download, Lock, Unlock, Check, X, AlertTriangle,
  RefreshCw, FileSpreadsheet, Calculator, Users, ChevronRight, Edit,
  CheckCircle, XCircle, Clock3, Shield, Eye, TrendingUp, BarChart3, AlertCircle,
  ListChecks, CheckSquare
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const API = API_URL;

export default function PayrollPage() {
  const { user } = useAuth();
  const { effectiveTenantId } = useTenant();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [activeTab, setActiveTab] = useState('periods');
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [pendingValidation, setPendingValidation] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [sites, setSites] = useState([]);

  // Filters
  const [filterShift, setFilterShift] = useState('all');
  const [filterSite, setFilterSite] = useState('all');

  // Dialogs
  const [createPeriodOpen, setCreatePeriodOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Form states
  const [newPeriod, setNewPeriod] = useState({ startDate: '', endDate: '', name: '' });
  const [validationForm, setValidationForm] = useState({ status: 'approved', comment: '', correctedRegularHours: '', correctedOvertimeHours: '' });

  // Bulk selection for validation
  const [bulkSelectedEntries, setBulkSelectedEntries] = useState([]);
  const [bulkValidationOpen, setBulkValidationOpen] = useState(false);
  const [bulkValidationForm, setBulkValidationForm] = useState({ status: 'approved', comment: '' });
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const tenantId = effectiveTenantId || user?.tenantId;

  /* ========== FETCHES ========== */
  const fetchPayPeriods = useCallback(async () => {
    if (!tenantId) return;
    try {
      const response = await axios.get(`${API}/payroll/periods?tenantId=${tenantId}`);
      setPayPeriods(response.data);
      if (response.data.length > 0 && !selectedPeriod) setSelectedPeriod(response.data[0]);
    } catch (error) { console.error('Failed to fetch pay periods:', error); }
  }, [tenantId, selectedPeriod]);

  const fetchShiftsAndSites = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [shiftsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/shifts?tenantId=${tenantId}`),
        axios.get(`${API}/sites?tenantId=${tenantId}`)
      ]);
      setShifts(shiftsRes.data);
      setSites(sitesRes.data);
    } catch (error) { console.error('Failed to fetch shifts/sites:', error); }
  }, [tenantId]);

  const fetchPendingValidation = useCallback(async () => {
    if (!tenantId) return;
    try {
      let url = `${API}/payroll/timesheets/pending-validation?tenantId=${tenantId}`;
      if (filterShift && filterShift !== 'all') url += `&shiftId=${filterShift}`;
      const response = await axios.get(url);
      setPendingValidation(response.data);
    } catch (error) { console.error('Failed to fetch pending validation:', error); }
  }, [tenantId, filterShift]);

  const fetchPayrollSummary = useCallback(async () => {
    if (!tenantId || !selectedPeriod) return;
    try {
      setLoading(true);
      let url = `${API}/payroll/summary?tenantId=${tenantId}&payPeriodId=${selectedPeriod.id}`;
      if (filterShift && filterShift !== 'all') url += `&shiftId=${filterShift}`;
      if (filterSite && filterSite !== 'all') url += `&siteId=${filterSite}`;
      const response = await axios.get(url);
      setPayrollSummary(response.data);
    } catch (error) {
      toast.error(fr ? 'Erreur lors du chargement' : 'Failed to load');
    } finally { setLoading(false); }
  }, [tenantId, selectedPeriod, filterShift, filterSite, fr]);

  useEffect(() => { fetchPayPeriods(); fetchShiftsAndSites(); }, [fetchPayPeriods, fetchShiftsAndSites]);

  useEffect(() => {
    if (activeTab === 'validation') fetchPendingValidation();
    else if (activeTab === 'summary' && selectedPeriod) fetchPayrollSummary();
  }, [activeTab, selectedPeriod, fetchPendingValidation, fetchPayrollSummary]);

  /* ========== ACTIONS ========== */
  const handleCreatePeriod = async () => {
    if (!newPeriod.startDate || !newPeriod.endDate) { toast.error(fr ? 'Dates requises' : 'Dates required'); return; }
    try {
      await axios.post(`${API}/payroll/periods`, { tenantId, startDate: newPeriod.startDate, endDate: newPeriod.endDate, name: newPeriod.name || null });
      toast.success(fr ? 'Période créée' : 'Period created');
      setCreatePeriodOpen(false); setNewPeriod({ startDate: '', endDate: '', name: '' }); fetchPayPeriods();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleGenerateTimesheets = async (periodId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/payroll/periods/${periodId}/generate`);
      toast.success(fr ? `${response.data.entriesCreated} entrées créées, ${response.data.entriesUpdated} mises à jour` : `${response.data.entriesCreated} created, ${response.data.entriesUpdated} updated`);
      fetchPendingValidation(); fetchPayrollSummary();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); } finally { setLoading(false); }
  };

  const handleLockPeriod = async (periodId) => {
    try {
      await axios.post(`${API}/payroll/periods/${periodId}/lock`, {});
      toast.success(fr ? 'Période verrouillée' : 'Period locked'); fetchPayPeriods();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleUnlockPeriod = async (periodId) => {
    try {
      await axios.post(`${API}/payroll/periods/${periodId}/unlock`, {});
      toast.success(fr ? 'Période déverrouillée' : 'Period unlocked'); fetchPayPeriods();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleValidateEntry = async (directEntry, directStatus) => {
    const entry = directEntry || selectedEntry;
    const status = directStatus || validationForm.status;
    if (!entry) return;
    try {
      await axios.post(`${API}/payroll/timesheets/${entry.id}/validate`, {
        status, comment: directStatus ? null : (validationForm.comment || null),
        correctedRegularHours: status === 'corrected' && validationForm.correctedRegularHours ? parseFloat(validationForm.correctedRegularHours) : null,
        correctedOvertimeHours: status === 'corrected' && validationForm.correctedOvertimeHours ? parseFloat(validationForm.correctedOvertimeHours) : null
      });
      toast.success(fr ? 'Validation enregistrée' : 'Validation saved');
      setValidationDialogOpen(false); setSelectedEntry(null);
      setValidationForm({ status: 'approved', comment: '', correctedRegularHours: '', correctedOvertimeHours: '' });
      fetchPendingValidation(); fetchPayrollSummary();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const openValidationDialog = (entry) => {
    setSelectedEntry(entry);
    setValidationForm({ status: 'approved', comment: '', correctedRegularHours: entry.regularHours?.toString() || '0', correctedOvertimeHours: entry.overtimeHours?.toString() || '0' });
    setValidationDialogOpen(true);
  };

  // Bulk validation handlers
  const toggleBulkEntry = (entryId) => {
    setBulkSelectedEntries(prev =>
      prev.includes(entryId) ? prev.filter(id => id !== entryId) : [...prev, entryId]
    );
  };

  const toggleAllEntries = () => {
    if (bulkSelectedEntries.length === pendingValidation.length) {
      setBulkSelectedEntries([]);
    } else {
      setBulkSelectedEntries(pendingValidation.map(e => e.id));
    }
  };

  const handleBulkValidation = async () => {
    if (bulkSelectedEntries.length === 0) return;
    setBulkProcessing(true);
    try {
      const res = await axios.post(`${API}/payroll/timesheets/bulk-validate`, {
        entryIds: bulkSelectedEntries,
        status: bulkValidationForm.status,
        comment: bulkValidationForm.comment || null,
      });
      toast.success(`${res.data.validatedCount} ${fr ? 'entrées validées' : 'entries validated'}`);
      setBulkValidationOpen(false);
      setBulkSelectedEntries([]);
      setBulkValidationForm({ status: 'approved', comment: '' });
      fetchPendingValidation();
      fetchPayrollSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setBulkProcessing(false);
    }
  };

  const buildPayrollExportUrl = (fmt) => {
    if (!selectedPeriod) return '#';
    return `${API}/payroll/export?tenantId=${tenantId}&payPeriodId=${selectedPeriod.id}&format=${fmt}`;
  };

  /* ========== COMPUTED ========== */
  const summaryKpis = useMemo(() => {
    if (!payrollSummary.length) return null;
    const totalRegular = payrollSummary.reduce((s, r) => s + (r.totalRegularHours || 0), 0);
    const totalOvertime = payrollSummary.reduce((s, r) => s + (r.totalOvertimeHours || 0), 0);
    const totalAbsences = payrollSummary.reduce((s, r) => s + (r.absenceDays || 0), 0);
    const totalLate = payrollSummary.reduce((s, r) => s + (r.totalLateMinutes || 0), 0);
    const pendingCount = payrollSummary.reduce((s, r) => s + (r.daysPending || 0), 0);
    const approvedCount = payrollSummary.reduce((s, r) => s + (r.daysApproved || 0), 0);
    const totalDays = payrollSummary.reduce((s, r) => s + (r.daysApproved || 0) + (r.daysPending || 0) + (r.daysRejected || 0), 0);
    return { totalRegular: Math.round(totalRegular * 10) / 10, totalOvertime: Math.round(totalOvertime * 10) / 10, totalAbsences, totalLate, pendingCount, approvedCount, totalDays, employeeCount: payrollSummary.length };
  }, [payrollSummary]);

  const periodStatusConfig = (status) => {
    const configs = {
      open: { label: fr ? 'Ouvert' : 'Open', cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800', icon: CheckCircle },
      locked: { label: fr ? 'Verrouillé' : 'Locked', cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800', icon: Lock },
    };
    return configs[status] || { label: status, cls: 'bg-slate-100 text-slate-700', icon: Clock };
  };

  const canManagePayroll = ['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'admin'].includes(user?.role);
  const canValidate = ['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_supervisor', 'admin'].includes(user?.role);

  const getValidationBadge = (status) => {
    const cfg = {
      pending: { cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400', label: fr ? 'En attente' : 'Pending' },
      approved: { cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400', label: fr ? 'Approuvé' : 'Approved' },
      rejected: { cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400', label: fr ? 'Refusé' : 'Rejected' },
      corrected: { cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400', label: fr ? 'Corrigé' : 'Corrected' },
    };
    const c = cfg[status] || cfg.pending;
    return <Badge className={`${c.cls} text-[10px]`}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="payroll-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white" data-testid="payroll-title">{fr ? 'Paie / RH' : 'Payroll / HR'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Gestion des périodes de paie et validation des heures' : 'Pay period management and hours validation'}</p>
        </div>
        {selectedPeriod && canManagePayroll && (
          <div className="flex gap-1.5">
            <DownloadButton url={buildPayrollExportUrl('csv')} className="gap-1 h-8 text-xs" data-testid="export-csv-btn"><Download size={12} /> CSV</DownloadButton>
            <DownloadButton url={buildPayrollExportUrl('excel')} className="gap-1 h-8 text-xs" data-testid="export-excel-btn"><FileSpreadsheet size={12} /> Excel</DownloadButton>
            <DownloadButton url={buildPayrollExportUrl('pdf')} className="gap-1 h-8 text-xs" data-testid="export-pdf-btn"><Download size={12} /> PDF</DownloadButton>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 h-9">
          <TabsTrigger value="periods" className="gap-1.5 text-xs" data-testid="tab-periods"><Calendar size={14} /> {fr ? 'Périodes' : 'Periods'}</TabsTrigger>
          <TabsTrigger value="validation" className="gap-1.5 text-xs" data-testid="tab-validation">
            <CheckCircle size={14} /> {fr ? 'Validation' : 'Validation'}
            {pendingValidation.length > 0 && <Badge variant="destructive" className="ml-1 text-[9px] px-1">{pendingValidation.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="summary" className="gap-1.5 text-xs" data-testid="tab-summary"><Calculator size={14} /> {fr ? 'Résumé Paie' : 'Payroll Summary'}</TabsTrigger>
        </TabsList>

        {/* ========== PERIODS TAB ========== */}
        <TabsContent value="periods" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{fr ? 'Périodes de paie' : 'Pay Periods'}</h2>
            {canManagePayroll && (
              <Button size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setCreatePeriodOpen(true)} data-testid="new-period-btn">
                <Calendar size={14} /> {fr ? 'Nouvelle période' : 'New Period'}
              </Button>
            )}
          </div>

          <div className="grid gap-3">
            {payPeriods.map((period) => {
              const cfg = periodStatusConfig(period.status);
              const StatusIcon = cfg.icon;
              return (
                <Card key={period.id} className={`cursor-pointer transition-all dark:border-slate-700 ${selectedPeriod?.id === period.id ? 'ring-2 ring-primary' : 'hover:border-primary/30'}`}
                  onClick={() => { setSelectedPeriod(period); setActiveTab('summary'); }} data-testid={`period-${period.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg border ${cfg.cls}`}><StatusIcon size={18} /></div>
                        <div>
                          <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-200">{period.name}</h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{period.startDate} → {period.endDate}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${cfg.cls} text-[10px]`}>{cfg.label}</Badge>
                        {canManagePayroll && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                              onClick={(e) => { e.stopPropagation(); handleGenerateTimesheets(period.id); }}
                              disabled={loading || period.status === 'locked'} data-testid={`calculate-${period.id}`}>
                              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {fr ? 'Calculer' : 'Calculate'}
                            </Button>
                            {period.status === 'open' ? (
                              <Button size="sm" className="h-7 text-xs gap-1 bg-red-600 hover:bg-red-700 text-white"
                                onClick={(e) => { e.stopPropagation(); handleLockPeriod(period.id); }} data-testid={`lock-${period.id}`}>
                                <Lock size={12} /> {fr ? 'Verrouiller' : 'Lock'}
                              </Button>
                            ) : user?.role === 'platform_superadmin' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                                onClick={(e) => { e.stopPropagation(); handleUnlockPeriod(period.id); }} data-testid={`unlock-${period.id}`}>
                                <Unlock size={12} /> {fr ? 'Déverrouiller' : 'Unlock'}
                              </Button>
                            )}
                          </div>
                        )}
                        <ChevronRight size={16} className="text-slate-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {payPeriods.length === 0 && (
              <Card className="p-8 text-center dark:border-slate-700"><Calendar className="mx-auto text-slate-400 mb-3" size={40} /><p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Aucune période de paie' : 'No pay periods'}</p></Card>
            )}
          </div>
        </TabsContent>

        {/* ========== VALIDATION TAB ========== */}
        <TabsContent value="validation" className="space-y-4 mt-4">
          {/* Alert banner */}
          {pendingValidation.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="validation-alert">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                {pendingValidation.length} {fr ? 'entrée(s) en attente de validation' : 'entry(ies) pending validation'}
                {pendingValidation.some(e => e.isPastDeadline) && (
                  <span className="text-red-600 dark:text-red-400 ml-1">({pendingValidation.filter(e => e.isPastDeadline).length} {fr ? 'en retard' : 'overdue'})</span>
                )}
              </p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{fr ? 'Validation des heures' : 'Hours Validation'}</h2>
            <div className="flex gap-2 items-center">
              <Select value={filterShift} onValueChange={setFilterShift}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-shift-validation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{fr ? 'Tous les quarts' : 'All shifts'}</SelectItem>
                  {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={fetchPendingValidation}><RefreshCw size={14} /></Button>
            </div>
          </div>

          {pendingValidation.length > 0 ? (
            <>
              {/* Bulk Action Bar */}
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-2.5 border dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={bulkSelectedEntries.length === pendingValidation.length && pendingValidation.length > 0}
                    onCheckedChange={toggleAllEntries}
                    data-testid="payroll-bulk-select-all"
                  />
                  <span className="text-xs text-slate-600 dark:text-slate-400">
                    {bulkSelectedEntries.length > 0
                      ? `${bulkSelectedEntries.length} / ${pendingValidation.length} ${fr ? 'sélectionné(s)' : 'selected'}`
                      : (fr ? 'Tout sélectionner' : 'Select all')}
                  </span>
                </div>
                {bulkSelectedEntries.length > 0 && (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      className="gap-1.5 h-7 text-xs bg-green-600 hover:bg-green-700"
                      onClick={() => { setBulkValidationForm({ status: 'approved', comment: '' }); setBulkValidationOpen(true); }}
                      data-testid="payroll-bulk-approve-btn"
                    >
                      <Check size={12} />
                      {fr ? 'Approuver' : 'Approve'} ({bulkSelectedEntries.length})
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className="gap-1.5 h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                      onClick={() => { setBulkValidationForm({ status: 'rejected', comment: '' }); setBulkValidationOpen(true); }}
                      data-testid="payroll-bulk-reject-btn"
                    >
                      <X size={12} />
                      {fr ? 'Rejeter' : 'Reject'} ({bulkSelectedEntries.length})
                    </Button>
                  </div>
                )}
              </div>

              <Card className="dark:border-slate-700" data-testid="validation-table">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                        <TableHead className="w-10 py-2"></TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'Employé' : 'Employee'}</TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'Date' : 'Date'}</TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'Entrée' : 'In'}</TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'Sortie' : 'Out'}</TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'Total' : 'Total'}</TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'H. sup.' : 'OT'}</TableHead>
                        <TableHead className="text-xs font-bold py-2">{fr ? 'Statut' : 'Status'}</TableHead>
                        <TableHead className="text-xs font-bold py-2 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingValidation.map((entry, idx) => {
                        const isChecked = bulkSelectedEntries.includes(entry.id);
                        return (
                          <TableRow key={entry.id} className={`${entry.isPastDeadline ? 'bg-red-50/50 dark:bg-red-950/10' : ''} ${isChecked ? 'bg-primary/5 dark:bg-primary/10' : (idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : '')}`} data-testid={`validation-row-${entry.id}`}>
                            <TableCell className="py-2 pl-3">
                              <Checkbox checked={isChecked} onCheckedChange={() => toggleBulkEntry(entry.id)} data-testid={`payroll-check-${entry.id}`} />
                            </TableCell>
                            <TableCell className="py-2">
                              <p className="font-medium text-sm text-slate-800 dark:text-slate-200">{entry.employeeName}</p>
                              <p className="text-[10px] text-slate-500">{entry.employeeCode}</p>
                            </TableCell>
                            <TableCell className="py-2 text-sm">
                              <span className="text-slate-700 dark:text-slate-300">{entry.date}</span>
                              {entry.isPastDeadline && <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[9px] ml-1">{fr ? 'Retard' : 'Late'}</Badge>}
                            </TableCell>
                            <TableCell className="py-2 text-sm text-slate-600 dark:text-slate-400">{entry.actualStart || '-'}</TableCell>
                            <TableCell className="py-2 text-sm text-slate-600 dark:text-slate-400">{entry.actualEnd || '-'}</TableCell>
                            <TableCell className="py-2 text-sm font-semibold text-slate-800 dark:text-slate-200">{entry.totalHours}h</TableCell>
                            <TableCell className="py-2">{entry.overtimeHours > 0 ? <Badge className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 text-[10px]">{entry.overtimeHours}h</Badge> : <span className="text-slate-400">-</span>}</TableCell>
                            <TableCell className="py-2">
                              {entry.isAbsent ? <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[10px]">{fr ? 'Absent' : 'Absent'}</Badge>
                                : entry.hasIncompleteData ? <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-[10px]">{fr ? 'Incomplet' : 'Incomplete'}</Badge>
                                : getValidationBadge(entry.validationStatus)}
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              {canValidate && (
                                <div className="flex gap-0.5 justify-end">
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20" onClick={() => handleValidateEntry(entry, 'approved')} data-testid={`approve-${entry.id}`}><Check size={14} /></Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/20" onClick={() => openValidationDialog(entry)} data-testid={`edit-${entry.id}`}><Edit size={14} /></Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20" onClick={() => handleValidateEntry(entry, 'rejected')} data-testid={`reject-${entry.id}`}><X size={14} /></Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="p-8 text-center dark:border-slate-700"><CheckCircle className="mx-auto text-green-500 dark:text-green-400 mb-3" size={40} /><p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Aucune entrée en attente de validation' : 'No entries pending validation'}</p></Card>
          )}
        </TabsContent>

        {/* ========== SUMMARY TAB ========== */}
        <TabsContent value="summary" className="space-y-4 mt-4">
          {/* Period header */}
          {selectedPeriod && (
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{selectedPeriod.name}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">{selectedPeriod.startDate} → {selectedPeriod.endDate}</p>
              </div>
              <div className="flex gap-2 items-center">
                <Select value={filterSite} onValueChange={setFilterSite}>
                  <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder={fr ? 'Tous sites' : 'All sites'} /></SelectTrigger>
                  <SelectContent><SelectItem value="all">{fr ? 'Tous' : 'All'}</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterShift} onValueChange={setFilterShift}>
                  <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder={fr ? 'Tous quarts' : 'All shifts'} /></SelectTrigger>
                  <SelectContent><SelectItem value="all">{fr ? 'Tous' : 'All'}</SelectItem>{shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Pre-lock alert */}
          {selectedPeriod?.status === 'open' && summaryKpis?.pendingCount > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="prelock-alert">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                <AlertCircle size={12} className="inline mr-1" />
                {summaryKpis.pendingCount} {fr ? 'jours non validés. Verrouillage impossible tant que des entrées sont en attente.' : 'days unvalidated. Cannot lock while entries are pending.'}
              </p>
            </div>
          )}

          {/* Summary KPIs */}
          {summaryKpis && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payroll-kpis">
              <div className="p-3 rounded-lg border dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/20">
                <div className="flex items-center gap-2 mb-1"><Clock size={14} className="text-blue-600 dark:text-blue-400" /><span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Heures totales' : 'Total Hours'}</span></div>
                <p className="text-xl font-bold text-slate-800 dark:text-white">{summaryKpis.totalRegular}h</p>
              </div>
              <div className="p-3 rounded-lg border dark:border-slate-700 bg-purple-50/50 dark:bg-purple-950/20">
                <div className="flex items-center gap-2 mb-1"><TrendingUp size={14} className="text-purple-600 dark:text-purple-400" /><span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Heures sup.' : 'Overtime'}</span></div>
                <p className="text-xl font-bold text-slate-800 dark:text-white">{summaryKpis.totalOvertime}h</p>
              </div>
              <div className="p-3 rounded-lg border dark:border-slate-700 bg-red-50/50 dark:bg-red-950/20">
                <div className="flex items-center gap-2 mb-1"><XCircle size={14} className="text-red-600 dark:text-red-400" /><span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Absences' : 'Absences'}</span></div>
                <p className="text-xl font-bold text-slate-800 dark:text-white">{summaryKpis.totalAbsences} {fr ? 'j' : 'd'}</p>
              </div>
              <div className="p-3 rounded-lg border dark:border-slate-700 bg-amber-50/50 dark:bg-amber-950/20">
                <div className="flex items-center gap-2 mb-1"><AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" /><span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Anomalies' : 'Anomalies'}</span></div>
                <p className="text-xl font-bold text-slate-800 dark:text-white">{summaryKpis.pendingCount + summaryKpis.totalAbsences}</p>
              </div>
            </div>
          )}

          {/* Validation progress */}
          {summaryKpis && summaryKpis.totalDays > 0 && (
            <Card className="dark:border-slate-700">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">{fr ? 'Progression validation' : 'Validation Progress'}</span>
                  <span className="text-xs text-slate-500">{summaryKpis.approvedCount}/{summaryKpis.totalDays} {fr ? 'validés' : 'validated'}</span>
                </div>
                <Progress value={summaryKpis.totalDays > 0 ? (summaryKpis.approvedCount / summaryKpis.totalDays) * 100 : 0} className="h-2" />
              </CardContent>
            </Card>
          )}

          {/* Summary Table */}
          {!selectedPeriod ? (
            <Card className="p-8 text-center dark:border-slate-700"><Calendar className="mx-auto text-slate-400 mb-3" size={40} /><p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Sélectionnez une période' : 'Select a period'}</p></Card>
          ) : payrollSummary.length > 0 ? (
            <Card className="dark:border-slate-700" data-testid="summary-table">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                      <TableHead className="text-xs font-bold py-2">{fr ? 'Employé' : 'Employee'}</TableHead>
                      <TableHead className="text-xs font-bold py-2">{fr ? 'Quart' : 'Shift'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-right">{fr ? 'H. norm.' : 'Regular'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-right">{fr ? 'H. sup.' : 'OT'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-right">{fr ? 'Total' : 'Total'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-right">{fr ? 'Absences' : 'Absences'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-right">{fr ? 'Retards' : 'Late'}</TableHead>
                      <TableHead className="text-xs font-bold py-2">{fr ? 'Validation' : 'Validation'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollSummary.map((row, idx) => (
                      <TableRow key={row.employeeId} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 ${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''}`} data-testid={`summary-row-${row.employeeId}`}>
                        <TableCell className="py-2">
                          <p className="font-medium text-sm text-slate-800 dark:text-slate-200">{row.employeeName}</p>
                          <p className="text-[10px] text-slate-500">{row.employeeCode}</p>
                        </TableCell>
                        <TableCell className="py-2 text-xs text-slate-600 dark:text-slate-400">{row.shift || '-'}</TableCell>
                        <TableCell className="py-2 text-right text-sm font-medium text-slate-800 dark:text-slate-200">{row.totalRegularHours}h</TableCell>
                        <TableCell className="py-2 text-right">{row.totalOvertimeHours > 0 ? <Badge className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 text-[10px]">{row.totalOvertimeHours}h</Badge> : <span className="text-slate-400">-</span>}</TableCell>
                        <TableCell className="py-2 text-right text-sm font-bold text-slate-900 dark:text-white">{row.totalHours}h</TableCell>
                        <TableCell className="py-2 text-right">{row.absenceDays > 0 ? <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[10px]">{row.absenceDays}</Badge> : <span className="text-slate-400">-</span>}</TableCell>
                        <TableCell className="py-2 text-right">{row.totalLateMinutes > 0 ? <span className="text-xs text-amber-600 dark:text-amber-400">{row.totalLateMinutes}min</span> : <span className="text-slate-400">-</span>}</TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-0.5">
                            {row.daysApproved > 0 && <Badge className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[9px] gap-0.5"><CheckCircle size={10} />{row.daysApproved}</Badge>}
                            {row.daysPending > 0 && <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-[9px] gap-0.5"><Clock3 size={10} />{row.daysPending}</Badge>}
                            {row.daysRejected > 0 && <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[9px] gap-0.5"><XCircle size={10} />{row.daysRejected}</Badge>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="p-8 text-center dark:border-slate-700"><Users className="mx-auto text-slate-400 mb-3" size={40} /><p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Aucune donnée. Cliquez "Calculer" pour générer.' : 'No data. Click "Calculate" to generate.'}</p></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ========== CREATE PERIOD DIALOG ========== */}
      <Dialog open={createPeriodOpen} onOpenChange={setCreatePeriodOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calendar size={18} /> {fr ? 'Nouvelle période de paie' : 'New Pay Period'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-xs">{fr ? 'Date de début' : 'Start Date'}</Label><Input type="date" value={newPeriod.startDate} onChange={e => setNewPeriod({ ...newPeriod, startDate: e.target.value })} className="h-8 text-sm" data-testid="period-start-date" /></div>
              <div className="space-y-2"><Label className="text-xs">{fr ? 'Date de fin' : 'End Date'}</Label><Input type="date" value={newPeriod.endDate} onChange={e => setNewPeriod({ ...newPeriod, endDate: e.target.value })} className="h-8 text-sm" data-testid="period-end-date" /></div>
            </div>
            <div className="space-y-2"><Label className="text-xs">{fr ? 'Nom (optionnel)' : 'Name (optional)'}</Label><Input value={newPeriod.name} onChange={e => setNewPeriod({ ...newPeriod, name: e.target.value })} placeholder={fr ? 'Ex: Période 1 - Janvier 2026' : 'Ex: Period 1 - Jan 2026'} className="h-8 text-sm" data-testid="period-name" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePeriodOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleCreatePeriod} data-testid="create-period-btn">{fr ? 'Créer' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== VALIDATION DIALOG ========== */}
      <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit size={18} /> {fr ? 'Validation des heures' : 'Validate Hours'}</DialogTitle>
            <DialogDescription>{selectedEntry && `${selectedEntry.employeeName} - ${selectedEntry.date}`}</DialogDescription>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg">
                <div><p className="text-[10px] text-slate-500 uppercase">{fr ? 'Entrée' : 'In'}</p><p className="font-medium text-sm text-slate-800 dark:text-slate-200">{selectedEntry.actualStart || '-'}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">{fr ? 'Sortie' : 'Out'}</p><p className="font-medium text-sm text-slate-800 dark:text-slate-200">{selectedEntry.actualEnd || '-'}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">{fr ? 'Heures' : 'Hours'}</p><p className="font-medium text-sm text-slate-800 dark:text-slate-200">{selectedEntry.totalHours}h</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">{fr ? 'H. sup.' : 'OT'}</p><p className="font-medium text-sm text-slate-800 dark:text-slate-200">{selectedEntry.overtimeHours}h</p></div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{fr ? 'Décision' : 'Decision'}</Label>
                <Select value={validationForm.status} onValueChange={v => setValidationForm({ ...validationForm, status: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">{fr ? 'Approuver' : 'Approve'}</SelectItem>
                    <SelectItem value="corrected">{fr ? 'Corriger' : 'Correct'}</SelectItem>
                    <SelectItem value="rejected">{fr ? 'Refuser' : 'Reject'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {validationForm.status === 'corrected' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label className="text-xs">{fr ? 'H. normales' : 'Regular'}</Label><Input type="number" step="0.5" value={validationForm.correctedRegularHours} onChange={e => setValidationForm({ ...validationForm, correctedRegularHours: e.target.value })} className="h-8 text-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs">{fr ? 'H. sup.' : 'OT'}</Label><Input type="number" step="0.5" value={validationForm.correctedOvertimeHours} onChange={e => setValidationForm({ ...validationForm, correctedOvertimeHours: e.target.value })} className="h-8 text-sm" /></div>
                </div>
              )}
              <div className="space-y-2"><Label className="text-xs">{fr ? 'Commentaire' : 'Comment'}</Label><Textarea value={validationForm.comment} onChange={e => setValidationForm({ ...validationForm, comment: e.target.value })} placeholder={fr ? 'Optionnel...' : 'Optional...'} rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidationDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={() => handleValidateEntry()} data-testid="confirm-validation-btn">{fr ? 'Valider' : 'Validate'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== BULK VALIDATION DIALOG ========== */}
      <Dialog open={bulkValidationOpen} onOpenChange={setBulkValidationOpen}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ListChecks size={18} className="text-primary" />
              {fr ? 'Validation groupée' : 'Bulk Validation'}
            </DialogTitle>
            <DialogDescription>
              {bulkSelectedEntries.length} {fr ? 'entrées sélectionnées' : 'entries selected'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="flex gap-2">
              <Button
                variant={bulkValidationForm.status === 'approved' ? 'default' : 'outline'}
                className={`flex-1 gap-1.5 ${bulkValidationForm.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => setBulkValidationForm(prev => ({ ...prev, status: 'approved' }))}
                data-testid="bulk-status-approved"
              >
                <Check size={14} />
                {fr ? 'Approuver tout' : 'Approve all'}
              </Button>
              <Button
                variant={bulkValidationForm.status === 'rejected' ? 'default' : 'outline'}
                className={`flex-1 gap-1.5 ${bulkValidationForm.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                onClick={() => setBulkValidationForm(prev => ({ ...prev, status: 'rejected' }))}
                data-testid="bulk-status-rejected"
              >
                <X size={14} />
                {fr ? 'Rejeter tout' : 'Reject all'}
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{fr ? 'Commentaire commun (optionnel)' : 'Common comment (optional)'}</Label>
              <Textarea
                value={bulkValidationForm.comment}
                onChange={(e) => setBulkValidationForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder={fr ? 'Commentaire pour toutes les entrées...' : 'Comment for all entries...'}
                className="min-h-[60px] text-sm"
                data-testid="bulk-validation-comment"
              />
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-2.5">
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                {fr
                  ? `${bulkSelectedEntries.length} entrée(s) seront ${bulkValidationForm.status === 'approved' ? 'approuvées' : 'rejetées'}. Action journalisée.`
                  : `${bulkSelectedEntries.length} entries will be ${bulkValidationForm.status}. Action logged.`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkValidationOpen(false)} className="h-9">{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button
              onClick={handleBulkValidation}
              disabled={bulkProcessing}
              className={`h-9 gap-1.5 ${bulkValidationForm.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-testid="confirm-bulk-validation-btn"
            >
              {bulkProcessing && <RefreshCw size={14} className="animate-spin" />}
              {fr ? `${bulkValidationForm.status === 'approved' ? 'Approuver' : 'Rejeter'} ${bulkSelectedEntries.length} entrée(s)` : `${bulkValidationForm.status === 'approved' ? 'Approve' : 'Reject'} ${bulkSelectedEntries.length} entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
